import React from 'react';
import { C } from './_overviewTheme';
import { downloadChartPng } from './_chartPng';

/**
 * src/pages/reports/_PanelChrome.js — the Chart/Table toggle, the white well
 * and the Download PNG button. Card P220 Part B1.
 *
 * ⭐ ONE COMPONENT FOR ELEVEN PANELS. Each panel supplies its chart and its
 * table; the chrome around them — which view is showing, what the PNG is
 * called, where the white well starts — is decided once. Eleven copies of a
 * toggle is eleven chances for one panel to default the other way.
 *
 * ⛔ THE TABLE IS NOT A FALLBACK, IT IS A VIEW. recharts does not lay out in
 * jsdom and does not print reliably; more importantly, a reader who wants the
 * number rather than the shape should not have to hover a bar. Chart is the
 * default where the card says so, and the toggle is always present.
 */

/** ⚠ Chart is the DEFAULT — the card lists it as such for every panel bar one. */
export function ChartTableToggle({ view, onChange, testid }) {
  const btn = (key, label) => (
    <button
      type="button"
      data-testid={`${testid}-view-${key}`}
      aria-pressed={view === key}
      onClick={() => onChange(key)}
      style={{
        padding: '3px 10px', fontSize: 11, fontWeight: 600, borderRadius: 999,
        cursor: 'pointer',
        border: `1px solid ${view === key ? C.primary : C.border}`,
        background: view === key ? C.primary : C.card,
        color: view === key ? '#fff' : C.body,
      }}
    >{label}</button>
  );
  return (
    <div role="group" aria-label="Chart or table" style={{ display: 'flex', gap: 4 }}>
      {btn('chart', 'Chart')}
      {btn('table', 'Table')}
    </div>
  );
}

/**
 * The white well a chart sits in — Card P222 item 1.
 *
 * ⛔⛔ WHY WHITE AND NOT THE SOFT CARD. P220 measured five of seven Okabe-Ito
 * fills below WCAG 1.4.11's 3:1 on the soft-grey ground; darkening them to
 * compensate collapsed blue against sky, which Okabe-Ito separates by
 * LIGHTNESS. On white the published hues work as designed and the 1px
 * outlines carry 1.4.11. It also means a PNG looks like what was on screen.
 */
export function ChartWell({ height, children, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        height, minWidth: 0, flexShrink: 0,
        background: C.well,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        padding: 6,
        boxSizing: 'border-box',
      }}
    >{children}</div>
  );
}

/**
 * Title row: the toggle and the PNG button.
 *
 * ⚠ The PNG button is HIDDEN while the table is showing rather than disabled
 * and silent: there is no chart to capture, and a button that does nothing is
 * the defect `_download.js` was written to stop repeating.
 */
export function PanelActions({ view, onChange, testid, panelRef, meta, onResult }) {
  const [busy, setBusy] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  const save = async () => {
    setBusy(true); setFailed(false);
    const ok = await downloadChartPng(panelRef && panelRef.current, meta);
    setBusy(false);
    if (!ok) setFailed(true);
    if (onResult) onResult(ok);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} className="ceo-no-print">
      <ChartTableToggle view={view} onChange={onChange} testid={testid} />
      {view === 'chart' ? (
        <button
          type="button" data-testid={`${testid}-png`} onClick={save} disabled={busy}
          style={{
            fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
            cursor: busy ? 'default' : 'pointer',
            border: `1px solid ${C.border}`, background: C.card, color: C.body,
          }}
        >{busy ? 'Saving…' : 'Download PNG'}</button>
      ) : null}
      {/* ⛔ A failure is SAID. The silent no-op is the bug this repo already
          shipped once on Export CSV. */}
      {failed ? (
        <span role="status" data-testid={`${testid}-png-failed`}
              style={{ fontSize: 11, color: C.highDeep }}>
          No chart to save.
        </span>
      ) : null}
    </div>
  );
}

/**
 * ⚠ Panels remember which view the reader chose, per panel, for the session.
 * Not persisted: a preference that outlives the question it was asked about
 * is its own kind of wrong.
 */
export function useChartView(initial = 'chart') {
  return React.useState(initial);
}
